import { useContext, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import Page from '../pages/Page'
import FetchContext from '../../context/fetch/FetchContext'
import { resolveErrorMessage } from '../../helpers/error.helper'
import { DASH, EMAIL_OUTCOME_LABELS, labelOf } from './emailLabels'

const EMPTY = {
    to: '',
    replyTo: '',
    subject: '',
    body: '',
    image: '',
}

function omitBlanks(form) {
    return Object.fromEntries(
        Object.entries(form).filter(([, value]) => value.trim() !== ''),
    )
}

function Diagnostics({ result }) {
    return (
        <MDBox data-testid="test-email-result" sx={{ mt: 3 }}>
            <MDTypography variant="h6" sx={{ mb: 1 }}>תוצאת הניסיון</MDTypography>
            <Table size="small">
                <TableHead sx={{ display: 'table-header-group' }}>
                    <TableRow>
                        <TableCell>נמען</TableCell>
                        <TableCell>מאת</TableCell>
                        <TableCell>מצב</TableCell>
                        <TableCell>הסבר</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {result.records.map((record) => (
                        <TableRow key={record.documentId}>
                            <TableCell>{record.toAddress}</TableCell>
                            <TableCell>{record.fromAddress}</TableCell>
                            <TableCell>{labelOf(EMAIL_OUTCOME_LABELS, record.outcome)}</TableCell>
                            <TableCell>{record.message ?? DASH}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </MDBox>
    )
}

// Sends through the same recorded path as any other message, so that what it proves is
// true of real mail rather than of a second code path.
export default function TestEmailPage() {
    const fetcher = useContext(FetchContext)
    const [form, setForm] = useState(EMPTY)
    const [result, setResult] = useState(null)
    const [error, setError] = useState(null)

    const sendMutation = useMutation({
        mutationFn: (data) => fetcher.authenticatedFetch({
            method: 'post',
            path: '/emails/test',
            data,
        }),
        onSuccess: (answer) => {
            setError(null)
            setResult(answer)
        },
        onError: (failure) => {
            setResult(null)
            setError(resolveErrorMessage(failure))
        },
    })

    const change = (name) => (event) => setForm({ ...form, [name]: event.target.value })

    const send = () => {
        if (form.to.trim() === '') {
            return
        }

        sendMutation.mutate(omitBlanks(form))
    }

    return (
        <Page className="test-email" title="בדיקת דואר" busy={sendMutation.isPending}>
            <MDBox sx={{ maxWidth: '48rem' }}>
                <Stack spacing={2}>
                    <TextField label="אל" value={form.to} onChange={change('to')} />
                    <TextField label="בכתובת למענה" value={form.replyTo} onChange={change('replyTo')} />
                    <TextField label="נושא" value={form.subject} onChange={change('subject')} />
                    <TextField
                        label="תוכן"
                        value={form.body}
                        onChange={change('body')}
                        multiline
                        minRows={6}
                    />
                    <TextField label="תמונה" value={form.image} onChange={change('image')} />
                    <MDBox>
                        <MDButton
                            variant="gradient"
                            color="info"
                            onClick={send}
                            disabled={sendMutation.isPending}
                        >
                            שליחה
                        </MDButton>
                    </MDBox>
                </Stack>

                {error != null && (
                    <MDTypography data-testid="test-email-error" variant="button" color="error" sx={{ mt: 3 }}>
                        {error}
                    </MDTypography>
                )}

                {result != null && <Diagnostics result={result} />}
            </MDBox>
        </Page>
    )
}
